import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useDataLayerValue } from '../../contextAPI/DataLayer';
import { BACKEND_URL } from '../../config';
import { Link } from 'react-router-dom';
import { useLoading } from '../../hooks/useLoading.js';
import { useNotifications } from '../../hooks/useNotifications';
import {
  getChecklistById,
  getEngagementByIdRequest,
  getEngagementPolicyById,
  getDocumentVersionsById,
  downloadVersionRequest
} from '../../constants/requests';
import CustomTable from '../../pages/Services/common/CustomTable';

const stylesObj = {
  container: {
    backgroundColor: 'whitesmoke',
    boxShadow: '2px 2px 10px grey',
    marginTop: '30px',
    padding: '40px 60px'
  },
  keyval: {
    display: 'flex'
  },
  key: {
    marginRight: '10px',
    fontWeight: 'bolder'
  },
  count: {
    fontWeight: 'bolder',
    marginRight: '10px'
  },
  history: {
    marginTop: '10px',
    fontWeight: 'bolder'
  },
  historyContainer: {
    margin: '0px 60px'
  },
  marker: {
    position: 'relative',
    top: '40px',
    right: '50px'
  }
};

function ActiveWorkflow() {
  const { id } = useParams();
  const [workflowHandlerDetails, setWorkflowHandlerDetails] = useState(null);
  const [empName, setEmpName] = useState(null);
  const [{ _id }] = useDataLayerValue();
  const { request } = useLoading();
  const [initiatorEmployeeId, setInitiatorEmployeeId] = useState(null);

  const getWorkflowDetails = async () => {
    document.title = 'Active Workflow Information';
    try {
      let response;

      response = await request(() =>
        axios.get(BACKEND_URL + '/active-workflow/' + id, {
          headers: {
            employeeid: _id
          }
        })
      );

      const { workflowHandlerId } = response.data.body;

      response = await request(() =>
        axios.get(BACKEND_URL + '/workflow-handler/' + workflowHandlerId, {
          headers: {
            employeeid: _id
          }
        })
      );

      const { initiator: initiatorEmployeeId } = response.data.body.data;
      const employee = await request(() =>
        axios.get(BACKEND_URL + '/employee/status/' + initiatorEmployeeId)
      );

      console.log(response.data.body);

      setInitiatorEmployeeId(initiatorEmployeeId);
      setEmpName(`${employee.data.body.fname} ${employee.data.body.lname}`);
      setWorkflowHandlerDetails(response.data.body);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getWorkflowDetails();
  }, []);

  return (
    <div>
      <h3>Active Workflow Information</h3>

      <div style={stylesObj.container}>
        <span style={stylesObj.keyval}>
          <p style={stylesObj.key}>Employee ID of the Initiator: </p>
          <p>{initiatorEmployeeId}</p>
        </span>
        <span style={stylesObj.keyval}>
          <p style={stylesObj.key}>Employee Name of the Initiator: </p>
          <p>{empName}</p>
        </span>
        {workflowHandlerDetails && (
          <DisplayInformationForWhichApprovalWasRequested
            workflowHandlerDetails={workflowHandlerDetails}
          />
        )}
      </div>
    </div>
  );
}

function DisplayInformationForWhichApprovalWasRequested({
  workflowHandlerDetails
}) {
  const { request } = useLoading();
  const { data, endPoint } = workflowHandlerDetails;
  const { method, url } = endPoint;
  const { showNotifications } = useNotifications();

  const [policy, setPolicy] = useState(null);
  const [engagement, setEngagement] = useState('');
  const [checklist, setChecklist] = useState(null);
  const [document, setDocument] = useState(null);
  const [documentVersions, setDocumentVersions] = useState(null);

  const getSCFTemplate = async () => {
    try {
      const policyId = data.RequestUrl.split('/')[2];

      const policyResponse = await request(() =>
        getEngagementPolicyById(policyId)
      );
      setPolicy(policyResponse.data.body);
    } catch (err) {
      console.log(err);
    }
  };

  const getEngagement = async () => {
    try {
      const engagementId = data.RequestUrl.split('/')[3];

      const engagementResponse = await request(() =>
        getEngagementByIdRequest(engagementId)
      );
      setEngagement(engagementResponse.data.body);
    } catch (err) {
      console.log(err);
    }
  };

  const getChecklist = async () => {
    try {
      let checklistId = null;

      if (data.RequestBody && data.RequestBody.checklistId) {
        checklistId = data.RequestBody.checklistId;
      } else if (
        data.responseData &&
        data.responseData.data &&
        data.responseData.data.checklistId
      ) {
        checklistId = data.responseData.data.checklistId;
      } else if (data.RequestUrl.includes('/checklist/')) {
        const urlParts = data.RequestUrl.split('/');
        const checklistIndex = urlParts.indexOf('checklist');
        if (
          checklistIndex !== -1 &&
          urlParts[checklistIndex + 1] &&
          urlParts[checklistIndex + 1] !== 'create'
        ) {
          checklistId = urlParts[checklistIndex + 1];
        }
      }

      const sourceDocumentId =
        data.RequestBody?.sourceDocumentId || data.RequestBody?.documentId;
      if (sourceDocumentId) {
        try {
          const versionsResponse = await request(() =>
            getDocumentVersionsById(sourceDocumentId)
          );
          if (versionsResponse.data && versionsResponse.data.body) {
            setDocumentVersions(versionsResponse.data.body);

            setDocument(versionsResponse.data.body);

            const workflowTimestamp = new Date(
              data.createdAt || data.timestamp
            );
            const versions = versionsResponse.data.body.versions || [];

            const relevantVersion =
              versions.find((version) => {
                const versionTimestamp = new Date(version.createdAt);
                return versionTimestamp <= workflowTimestamp;
              }) || versions[versions.length - 1];

            if (relevantVersion) {
              showNotifications('success', `Document version loaded`, 3000);
            }
          }
        } catch (err) {
          showNotifications(
            'error',
            'Failed to load document version information',
            5000
          );
        }
      }

      if (checklistId && checklistId !== 'create') {
        const checklistResponse = await request(() =>
          getChecklistById(checklistId)
        );
        setChecklist(checklistResponse.data.data);
      }
    } catch (err) {
      showNotifications(
        'error',
        'No checklistId found - this is likely a pending checklist request',
        5000
      );
    }
  };

  const handleDownloadVersion = async (documentId, versionId, fileName) => {
    try {
      const response = await request(() =>
        downloadVersionRequest(documentId, versionId)
      );

      if (response.data && response.data.body && response.data.body.signedUrl) {
        const link = window.document.createElement('a');
        link.href = response.data.body.signedUrl;
        link.download = fileName || `document_${versionId.substring(0, 8)}.pdf`;
        link.target = '_blank';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);

        showNotifications(
          'success',
          `Document version ${versionId.substring(
            0,
            8
          )}... downloaded successfully`,
          3000
        );
      } else {
        showNotifications(
          'error',
          'Failed to get download URL - invalid response format',
          5000
        );
      }
    } catch (err) {
      showNotifications('error', 'Failed to download document version', 5000);
    }
  };

  useEffect(() => {
    switch (url) {
      case '/policy/:id': {
        getSCFTemplate();
        break;
      }
      case '/itgc/engagement/:id': {
        getEngagement();
        break;
      }
      case '/checklist/create': {
        getChecklist();
        break;
      }
    }
  }, []);

  if (method === 'PATCH' || method === 'POST' || method === 'GET') {
    switch (url) {
      case '/itgc/engagement/:id': {
        const engagementId = data.RequestUrl.split('/')[3];
        return (
          <span style={stylesObj.keyval}>
            <p style={stylesObj.key}>Link:</p>
            <Link
              to={'/services/engagement/' + engagementId + '/understanding'}
            >
              <p>{engagement?.projectId?.projectName ?? ''}</p>
            </Link>
          </span>
        );
      }

      case '/policy/:id': {
        try {
          return (
            <>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Policy Name:</p>
                <p>{policy.name}</p>
              </span>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Policy Type:</p>
                <p>{policy.type}</p>
              </span>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Aumyaa Preparer:</p>
                <p>{policy.aumyaaPreparer}</p>
              </span>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Aumyaa Reviewer:</p>
                <p>{policy.aumyaaReviewer}</p>
              </span>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Aumyaa Approver:</p>
                <p>{policy.aumyaaApprover}</p>
              </span>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Client Preparer:</p>
                <p>{policy.clientPreparer}</p>
              </span>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Client Reviewer:</p>
                <p>{policy.clientReviewer}</p>
              </span>
              <span style={stylesObj.keyval}>
                <p style={stylesObj.key}>Client Approver:</p>
                <p>{policy.clientApprover}</p>
              </span>
              <CustomTable
                fieldsMeta={policy.SCFTemplate.meta.fields}
                tableMeta={policy.SCFTemplate.meta.LastApprovedPolicyControls}
                rows={data.RequestBody.lastApprovedSCF.controls}
              />
            </>
          );
        } catch (err) {
          console.log(err);
        }
        break;
      }

      case '/checklist/create': {
        return (
          <>
            <span style={stylesObj.keyval}>
              <p style={stylesObj.key}>Workflow Type:</p>
              <p>Checklist Generation</p>
            </span>
            <span style={stylesObj.keyval}>
              <p style={stylesObj.key}>Request URL:</p>
              <p>{data.RequestUrl}</p>
            </span>
            {checklist ? (
              <>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Checklist ID:</p>
                  <p>{checklist._id}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Status:</p>
                  <p>{checklist?.status}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Checklist Type:</p>
                  <p>{checklist?.checklistType}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Source Document:</p>
                  <p>{checklist?.metadata?.sourceFileName || 'N/A'}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Generated Document:</p>
                  <p>{checklist?.metadata?.generatedFileName || 'N/A'}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Total Items:</p>
                  <p>{checklist?.metadata?.totalItems || 'N/A'}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Processing Time:</p>
                  <p>
                    {checklist?.metadata?.processingTime
                      ? `${checklist.metadata.processingTime}ms`
                      : 'N/A'}
                  </p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>AI Model:</p>
                  <p>{checklist?.metadata?.aiModel || 'N/A'}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Version:</p>
                  <p>{checklist?.metadata?.version || 'N/A'}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Created At:</p>
                  <p>
                    {checklist?.createdAt
                      ? new Date(checklist.createdAt).toLocaleString()
                      : 'N/A'}
                  </p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Updated At:</p>
                  <p>
                    {checklist?.updatedAt
                      ? new Date(checklist.updatedAt).toLocaleString()
                      : 'N/A'}
                  </p>
                </span>
                {checklist.errorDetails && (
                  <span style={stylesObj.keyval}>
                    <p style={stylesObj.key}>Error Message:</p>
                    <p style={{ color: 'red' }}>
                      {checklist.errorDetails.message}
                    </p>
                  </span>
                )}
              </>
            ) : (
              <>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Requested By:</p>
                  <p>{data.initiator || 'Unknown User'}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Source Document:</p>
                  <div>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                      {document?.name ||
                        document?.fileName ||
                        document?.originalName ||
                        'Loading document details...'}
                    </p>
                    {document?.size && (
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#666',
                          marginBottom: '3px'
                        }}
                      >
                        Size: {(document.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    )}
                    {document?.uploadedAt && (
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#666',
                          marginBottom: '3px'
                        }}
                      >
                        Uploaded:{' '}
                        {new Date(document.uploadedAt).toLocaleDateString()}
                      </p>
                    )}
                    {documentVersions && documentVersions.versions && (
                      <div
                        style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px'
                        }}
                      >
                        <p
                          style={{
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '4px'
                          }}
                        >
                          Version Information:
                        </p>
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginBottom: '2px'
                          }}
                        >
                          Total Versions: {documentVersions.versions.length}
                        </p>
                        {(() => {
                          const workflowTimestamp = new Date(
                            data.createdAt || data.timestamp
                          );
                          const versions = documentVersions.versions || [];
                          const relevantVersion =
                            versions.find((version) => {
                              const versionTimestamp = new Date(
                                version.createdAt
                              );
                              return versionTimestamp <= workflowTimestamp;
                            }) || versions[versions.length - 1];

                          if (relevantVersion) {
                            return (
                              <div>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '4px'
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: '12px',
                                      color: '#2196F3',
                                      fontWeight: 'bold',
                                      margin: 0
                                    }}
                                  >
                                    Version Used:{' '}
                                    {relevantVersion.versionId.substring(0, 8)}
                                    ...
                                  </p>
                                  <button
                                    onClick={() =>
                                      handleDownloadVersion(
                                        data.RequestBody?.sourceDocumentId ||
                                          data.RequestBody?.documentId,
                                        relevantVersion.versionId,
                                        document?.name ||
                                          document?.fileName ||
                                          document?.originalName
                                      )
                                    }
                                    style={{
                                      fontSize: '10px',
                                      padding: '4px 8px',
                                      backgroundColor: '#2196F3',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontWeight: 'bold'
                                    }}
                                    onMouseOver={(e) =>
                                      (e.target.style.backgroundColor =
                                        '#1976D2')
                                    }
                                    onMouseOut={(e) =>
                                      (e.target.style.backgroundColor =
                                        '#2196F3')
                                    }
                                  >
                                    📥 Download
                                  </button>
                                </div>
                                <p style={{ fontSize: '11px', color: '#666' }}>
                                  Created:{' '}
                                  {new Date(
                                    relevantVersion.createdAt
                                  ).toLocaleString()}
                                </p>
                                {relevantVersion.comments && (
                                  <p
                                    style={{
                                      fontSize: '11px',
                                      color: '#666',
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    {relevantVersion.comments}
                                  </p>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    <p style={{ fontSize: '12px', color: '#888' }}>
                      ID:{' '}
                      {data.RequestBody?.sourceDocumentId ||
                        data.RequestBody?.documentId ||
                        'N/A'}
                    </p>
                  </div>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Checklist Type:</p>
                  <p>{data.RequestBody?.checklistType || 'REGULATION'}</p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Processing Request:</p>
                  <p style={{ color: '#2196F3', fontStyle: 'italic' }}>
                    Generate compliance checklist from the uploaded document
                    using AI analysis
                  </p>
                </span>
                <span style={stylesObj.keyval}>
                  <p style={stylesObj.key}>Status:</p>
                  <p style={{ color: 'orange', fontWeight: 'bold' }}>
                    Pending Approval
                  </p>
                </span>
              </>
            )}
          </>
        );
      }
    }
  }

  return null;
}

export default ActiveWorkflow;
